// Per-account token load — the page's densest table, and the one place the
// per-endpoint spend behind a percentage can be opened up.
//
// Rows sort by tightest bucket, so the account closest to a limit always leads
// regardless of which bucket is under pressure. The expanded drill-down is the
// batching check: portfolio/orders and portfolio/orders/queue_positions should
// track 1:1 however many orders are resting, because one batched queue call per
// poll covers the whole portfolio. A queue count well above the orders count
// means un-batched per-order reads have crept back in.

import { Fragment } from 'react';
import { fmtAge, orgIdOf, shortKey } from './healthFormat';
import { loadTone, type Enriched } from './tokenLoad';

export function AccountLoadTable({
  sorted,
  orgNames,
  selectedOrg,
  openAccount,
  setOpenAccount,
}: {
  /** Already filtered to the selection and sorted by tightest bucket. */
  sorted: Enriched[];
  orgNames: Record<string, string>;
  selectedOrg: string;
  /** The account key whose per-endpoint breakdown is open, or null. */
  openAccount: string | null;
  setOpenAccount: (key: string | null) => void;
}) {
  return (
  <section className="border-border rounded-lg border p-4">
    <div className="mb-3 flex items-baseline justify-between">
      <h2 className="text-foreground text-sm font-semibold">Per-account token load</h2>
      <span className="text-muted-foreground text-[11px]">
        tokens/sec over the relay&apos;s 10s window · sorted by tightest bucket · click a row for the
        per-endpoint spend
      </span>
    </div>
    {sorted.length === 0 ? (
      <p className="text-muted-foreground py-6 text-center text-xs">
        {selectedOrg === 'all'
          ? 'No active account clients right now (the relay caches one per org pool / personal account; an idle relay shows none).'
          : 'No active account for this org right now.'}
      </p>
    ) : (
      <div className="overflow-x-auto">
        <table className="w-full text-left text-xs">
          <thead className="text-muted-foreground border-border border-b">
            <tr>
              <th className="py-1.5 pr-3 font-medium">Account</th>
              <th className="px-3 font-medium">Tier</th>
              <th className="px-3 text-right font-medium" title="read tokens/sec · % of read limit">Read</th>
              <th className="px-3 text-right font-medium" title="write tokens/sec · % of write limit">Write</th>
              <th
                className="px-3 text-right font-medium"
                title="Kalshi's own /account/limits statement: read/write refill per second (+ burst capacity). Sampled while this page is open; the % columns score against these when present."
              >
                Bucket (live)
              </th>
              <th className="px-3 text-center font-medium" title="read backoff active — writes still flow">429</th>
              <th className="px-3 text-right font-medium" title="backoff escalation depth">Strikes</th>
              <th className="px-3 text-right font-medium" title="ms until backoff clears">Clears in</th>
              <th className="px-3 text-right font-medium" title="live / total keys">Keys</th>
              <th className="px-3 text-right font-medium">Age</th>
            </tr>
          </thead>
          <tbody className="font-mono">
            {sorted.map((e) => {
              const k = shortKey(e.key);
              const oid = orgIdOf(e.key);
              const name = oid ? orgNames[oid] ?? k.id : k.id;
              const rl = e.rateLimit;
              const lp = e.limits?.parsed ?? null;
              const isOpen = openAccount === e.key;
              const byEndpoint = e.requests?.byEndpoint ?? [];
              const totalTok = byEndpoint.reduce((s, be) => s + be.tokens, 0);
              return (
                <Fragment key={e.key}>
                  <tr
                    onClick={() => setOpenAccount(isOpen ? null : e.key)}
                    className={`border-border/50 hover:bg-muted/40 cursor-pointer border-b ${rl.throttled ? 'bg-red-500/10' : ''}`}
                    title="Click for the per-endpoint token spend"
                  >
                    <td className="py-1.5 pr-3">
                      <span className="text-muted-foreground">{isOpen ? '▾' : '▸'} {k.scope}</span>
                      <span className="text-foreground ml-1">{name}</span>
                    </td>
                    <td className="px-3">
                      <span className={e.tier === 'advanced' ? 'text-green-400' : 'text-amber-400'}>
                        {e.tier ?? 'basic'}
                      </span>
                    </td>
                    <td
                      className={`px-3 text-right ${loadTone(e.readPct, rl.throttled)}`}
                      title={`${e.readTokS.toFixed(0)} of ${e.lim.read} read tok/s${e.measured ? ' (measured per-endpoint costs)' : ' (≈ requests × 10 estimate)'}`}
                    >
                      {e.readTokS.toFixed(0)}
                      <span className="text-muted-foreground">t/s</span>{' '}
                      <span className="font-semibold">{e.readPct}%</span>
                    </td>
                    <td
                      className={`px-3 text-right ${loadTone(e.writePct)}`}
                      title={`${e.writeTokS.toFixed(0)} of ${e.lim.write} write tok/s — writes are never paused by the read backoff`}
                    >
                      {e.writeTokS.toFixed(0)}
                      <span className="text-muted-foreground">t/s</span>{' '}
                      <span className="font-semibold">{e.writePct}%</span>
                    </td>
                    <td className="px-3 text-right">
                      {lp?.read && lp?.write ? (
                        <span
                          title={`Kalshi /account/limits (${fmtAge(e.limits?.ageMs ?? 0)} old): tier ${lp.usageTier} · read ${lp.read.refillRate}/s burst ${lp.read.bucketCapacity} · write ${lp.write.refillRate}/s burst ${lp.write.bucketCapacity}`}
                        >
                          <span className="text-foreground">{lp.read.refillRate}r/{lp.write.refillRate}w</span>
                          <span className="text-muted-foreground"> ✓</span>
                        </span>
                      ) : (
                        <span className="text-muted-foreground" title="No /account/limits sample yet — % scored against the tier-map guess">
                          —
                        </span>
                      )}
                    </td>
                    <td className="px-3 text-center">
                      {rl.throttled ? (
                        <span className="rounded bg-red-600/80 px-1.5 py-0.5 text-[10px] font-bold text-white">
                          THROTTLED
                        </span>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </td>
                    <td className={`px-3 text-right ${rl.rateLimitStrikes > 0 ? 'text-red-400' : 'text-muted-foreground'}`}>
                      {rl.rateLimitStrikes}
                    </td>
                    <td className="text-muted-foreground px-3 text-right">
                      {rl.msUntilClear > 0 ? `${(rl.msUntilClear / 1000).toFixed(1)}s` : '—'}
                    </td>
                    <td className={`px-3 text-right ${rl.quarantinedCount > 0 ? 'text-amber-400' : 'text-muted-foreground'}`}>
                      {rl.channelCount - rl.quarantinedCount}/{rl.channelCount}
                    </td>
                    <td className="text-muted-foreground px-3 text-right">{fmtAge(e.ageMs)}</td>
                  </tr>
                  {isOpen && (
                    <tr className="border-border/50 border-b">
                      <td colSpan={10} className="bg-muted/20 px-4 py-2">
                        {byEndpoint.length === 0 ? (
                          <span className="text-muted-foreground text-[11px]">
                            No per-endpoint data (relay predates token accounting, or no requests in the
                            10s window).
                          </span>
                        ) : (
                          <>
                            <table className="w-auto min-w-[520px] text-left text-[11px]">
                              <thead className="text-muted-foreground">
                                <tr>
                                  <th className="py-1 pr-4 font-medium">Endpoint</th>
                                  <th className="pr-4 font-medium">r/w</th>
                                  <th className="pr-4 text-right font-medium">req / 10s</th>
                                  <th className="pr-4 text-right font-medium">tok/s</th>
                                  <th className="text-right font-medium">% of spend</th>
                                </tr>
                              </thead>
                              <tbody>
                                {byEndpoint.map((be) => (
                                  <tr key={`${be.rw}:${be.endpoint}`}>
                                    <td className="text-foreground py-0.5 pr-4">{be.endpoint}</td>
                                    <td className={`pr-4 ${be.rw === 'write' ? 'text-sky-400' : 'text-muted-foreground'}`}>
                                      {be.rw}
                                    </td>
                                    <td className="text-foreground pr-4 text-right">{be.count}</td>
                                    <td className="text-foreground pr-4 text-right">
                                      {(be.tokens / ((e.requests?.windowMs ?? 10000) / 1000)).toFixed(1)}
                                    </td>
                                    <td className="text-muted-foreground text-right">
                                      {totalTok > 0 ? Math.round((be.tokens / totalTok) * 100) : 0}%
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                            <p className="text-muted-foreground mt-1.5 text-[11px]">
                              Batching check: <span className="text-foreground">portfolio/orders</span> and{' '}
                              <span className="text-foreground">portfolio/orders/queue_positions</span> counts
                              should match ~1:1 regardless of how many orders are resting — one batched queue
                              call per poll tick is the whole portfolio. A queue count ≫ orders count means
                              un-batched per-order reads are sneaking in.
                            </p>
                          </>
                        )}
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    )}
  </section>
  );
}
